/**
 * Verifier Service
 * API integration for verifier profile management, reviews, portfolio, and documents
 */

import api from './api';

const verifierService = {
    
    // ============================================
    // VERIFIER PROFILE MANAGEMENT
    // ============================================
    
    /**
     * Get all verifiers with filtering and pagination (Public directory)
     */
    getAllVerifiers: async (filters = {}, pagination = {}) => {
        try {
            const params = {
                ...filters,
                ...pagination
            };
            
            const response = await api.get('/verifiers', { params });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to fetch verifiers');
        }
    },
    
    /**
     * Get verifier profile by ID (Public view)
     */
    getVerifierById: async (verifierId) => {
        try {
            const response = await api.get(`/verifiers/${verifierId}`);
            return response.data.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to fetch verifier profile');
        }
    },
    
    /**
     * Get own verifier profile
     */
    getMyProfile: async () => {
        try {
            const response = await api.get('/verifiers/profile/me');
            return response.data.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to fetch profile');
        }
    },
    
    /**
     * Get profile completeness for own profile
     */
    getMyProfileCompleteness: async () => {
        try {
            const response = await api.get('/verifiers/profile/me/completeness');
            return response.data.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to fetch profile completeness');
        }
    },
    
    /**
     * Create verifier profile
     */
    createProfile: async (profileData) => {
        try {
            const response = await api.post('/verifiers/profile', profileData);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to create profile');
        }
    },
    
    /**
     * Update verifier profile
     */
    updateProfile: async (profileId, updateData) => {
        try {
            const response = await api.put(`/verifiers/profile/${profileId}`, updateData);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to update profile');
        }
    },
    
    /**
     * Update own verifier profile
     */
    updateMyProfile: async (updateData) => {
        try {
            const response = await api.put('/verifiers/profile/me', updateData);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to update profile');
        }
    },
    
    /**
     * Update verifier availability
     */
    updateAvailability: async (profileId, availabilityData) => {
        try {
            // Validate availability status against database constraint
            const validStatuses = ['available', 'busy', 'offline'];
            if (availabilityData.availabilityStatus && !validStatuses.includes(availabilityData.availabilityStatus)) {
                // Convert 'unavailable' to 'offline' for backwards compatibility
                if (availabilityData.availabilityStatus === 'unavailable') {
                    availabilityData.availabilityStatus = 'offline';
                } else {
                    throw new Error(`Invalid availability status. Must be one of: ${validStatuses.join(', ')}`);
                }
            }

            const response = await api.put(
                `/verifiers/profile/${profileId}/availability`,
                availabilityData
            );
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to update availability');
        }
    },
    
    /**
     * Find available verifiers for assignment
     */
    findAvailableVerifiers: async (criteria) => {
        try {
            const response = await api.post('/verifiers/find-available', criteria);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to find available verifiers');
        }
    },
    
    /**
     * Get verifier statistics
     */
    getStatistics: async (verifierId) => {
        try {
            const response = await api.get(`/verifiers/${verifierId}/statistics`);
            return response.data.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to fetch statistics');
        }
    },
    
    // ============================================
    // VERIFIER DOCUMENTS
    // ============================================
    
    /**
     * Add document to verifier profile
     */
    addDocument: async (verifierId, documentData) => {
        try {
            const response = await api.post(
                `/verifiers/profile/${verifierId}/documents`,
                documentData
            );
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to add document');
        }
    },
    
    /**
     * Get all documents for verifier
     */
    getDocuments: async (verifierId) => {
        try {
            const response = await api.get(`/verifiers/profile/${verifierId}/documents`);
            return response.data.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to fetch documents');
        }
    },
    
    /**
     * Delete document
     */
    deleteDocument: async (verifierId, documentId) => {
        try {
            const response = await api.delete(
                `/verifiers/profile/${verifierId}/documents/${documentId}`
            );
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to delete document');
        }
    },
    
    /**
     * Verify document (Admin only)
     */
    verifyDocument: async (verifierId, documentId, notes = null) => {
        try {
            const response = await api.post(
                `/verifiers/profile/${verifierId}/documents/${documentId}/verify`,
                { notes }
            );
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to verify document');
        }
    },
    
    // ============================================
    // VERIFIER REVIEWS
    // ============================================
    
    /**
     * Get reviews for a verifier
     */
    getReviews: async (verifierId, options = {}) => {
        try {
            const params = {
                page: options.page || 1,
                limit: options.limit || 10,
                minRating: options.minRating
            };
            
            const response = await api.get(`/verifiers/${verifierId}/reviews`, { params });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to fetch reviews');
        }
    },
    
    /**
     * Add review for verifier
     */
    addReview: async (verifierId, reviewData) => {
        try {
            const response = await api.post(`/verifiers/${verifierId}/reviews`, reviewData);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to submit review');
        }
    },
    
    /**
     * Add response to review (Verifier only)
     */
    addReviewResponse: async (verifierId, reviewId, responseText) => {
        try {
            const response = await api.post(
                `/verifiers/${verifierId}/reviews/${reviewId}/response`,
                { response: responseText }
            );
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to add response');
        }
    },
    
    // ============================================
    // VERIFIER PORTFOLIO
    // ============================================
    
    /**
     * Get portfolio items
     */
    getPortfolio: async (verifierId) => {
        try {
            // Use authenticated owner route so private items are visible in management view
            const response = await api.get(`/verifiers/profile/${verifierId}/portfolio`);
            return response.data.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to fetch portfolio');
        }
    },
    
    /**
     * Add portfolio item
     */
    addPortfolioItem: async (verifierId, portfolioData) => {
        try {
            const response = await api.post(
                `/verifiers/profile/${verifierId}/portfolio`,
                portfolioData
            );
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to add portfolio item');
        }
    },
    
    /**
     * Update portfolio item
     */
    updatePortfolioItem: async (verifierId, itemId, updateData) => {
        try {
            const response = await api.put(
                `/verifiers/profile/${verifierId}/portfolio/${itemId}`,
                updateData
            );
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to update portfolio item');
        }
    },
    
    /**
     * Delete portfolio item
     */
    deletePortfolioItem: async (verifierId, itemId) => {
        try {
            const response = await api.delete(
                `/verifiers/profile/${verifierId}/portfolio/${itemId}`
            );
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to delete portfolio item');
        }
    },
    
    // ============================================
    // TIER MANAGEMENT
    // ============================================
    
    /**
     * Request tier upgrade to professional
     */
    requestTierUpgrade: async (verifierId) => {
        try {
            const response = await api.post(
                `/verifiers/profile/${verifierId}/request-upgrade`
            );
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to request tier upgrade');
        }
    },
    
    /**
     * Approve tier upgrade (Admin only)
     */
    approveTierUpgrade: async (verifierId) => {
        try {
            const response = await api.post(
                `/verifiers/profile/${verifierId}/approve-upgrade`
            );
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to approve tier upgrade');
        }
    },
    
    // ============================================
    // HELPER FUNCTIONS FOR DASHBOARD
    // ============================================
    
    /**
     * Fetch dashboard data (combines multiple API calls)
     */
    fetchDashboard: async (userId) => {
        try {
            // Get verifier profile
            const profileResponse = await verifierService.getMyProfile();
            
            // Get statistics
            let stats = {};
            if (profileResponse?.id) {
                try {
                    stats = await verifierService.getStatistics(profileResponse.id);
                } catch (error) {
                    console.warn('Failed to fetch statistics:', error);
                }
            }
            
            // Return combined dashboard data
            return {
                profile: {
                    id: profileResponse.id,
                    name: `${profileResponse.first_name} ${profileResponse.last_name}`,
                    avatar: profileResponse.profile_image_url,
                    rating: parseFloat(profileResponse.average_rating) || 0,
                    reviewCount: profileResponse.total_reviews || 0,
                    tier: profileResponse.tier,
                    availability: profileResponse.availability_status,
                    verificationStatus: profileResponse.verification_status
                },
                stats: {
                    totalVerifications: profileResponse.total_verifications || 0,
                    completedVerifications: profileResponse.completed_verifications || 0,
                    averageRating: parseFloat(profileResponse.average_rating) || 0,
                    totalReviews: profileResponse.total_reviews || 0,
                    completionRate: parseFloat(profileResponse.completion_rate) || 0,
                    currentAssignments: profileResponse.current_assignments || 0,
                    availableSlots: (profileResponse.max_daily_assignments || 5) - (profileResponse.current_assignments || 0)
                },
                assignments: [], // TODO: Integrate with verification requests API
                todaySchedule: [], // TODO: Integrate with verification requests API
                recentRequests: [], // TODO: Integrate with verification requests API
                requests: [] // TODO: Integrate with verification requests API
            };
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch dashboard data');
        }
    }
};

export default verifierService;
